import {
  Autocomplete,
  FormControl,
  Grid,
  InputLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { Controller } from 'react-hook-form';

import useSubSourceTypeSelector from '../CloudSourceHooks/useSubSourceTypeSelector';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { selectedSubSourceTypeAtom } from '../SourceCreate.store';
import useStyles from '../SourceCreate.styles';
import { SourceDialogMode } from '../SourceCreate.types';

// SubSourceTypeSelector component is used to support:
// - Mimecast (Version 1 / Version 2)
// - Cisco Secure Endpoint (Setup Streaming Queue: Yes/No)

const SubSourceTypeSelector = () => {
  const { mode: themeMode } = useColorScheme();
  const { classes } = useStyles({ themeMode });
  const {
    control,
    type,
    options,
    defaultValue,
    label,
    hasError,
    errorMessage,
    shouldRenderSubSourceType,
    mode
  } = useSubSourceTypeSelector();
  const [selectedSubSourceType, setSelectedSubSourceType] = useAtom(
    selectedSubSourceTypeAtom
  );

  // only update atom when user explicitly picks something
  const handleSelectChange = useCallback(
    (newValue: string | null, onChange: (val: string | null) => void) => {
      onChange(newValue);
      if (newValue !== null) {
        setSelectedSubSourceType(newValue);
      }
    },
    [setSelectedSubSourceType]
  );

  if (!shouldRenderSubSourceType) return null;

  return (
    <Grid size={{ xs: 12, sm: 12, md: 4 }}>
      <InputLabel>
        {label} <span>*</span>
      </InputLabel>

      {type === 'select' && (
        <FormControl fullWidth>
          <Controller
            control={control}
            name="sub_source_type"
            rules={{ required: `Select ${label}` }}
            render={({ field: { onChange } }) => (
              <Autocomplete
                className={classes.multiSelectForm}
                disableClearable
                options={options}
                getOptionLabel={(option) => option || ''}
                value={
                  Array.isArray(selectedSubSourceType)
                    ? selectedSubSourceType[0] || defaultValue
                    : selectedSubSourceType || defaultValue
                }
                disabled={mode === SourceDialogMode.Edit}
                onChange={(_, newValue) =>
                  handleSelectChange(newValue, onChange)
                }
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      {option}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} error={hasError} />
                )}
              />
            )}
          />
          {hasError && <ErrorMessage message={errorMessage} />}
        </FormControl>
      )}

      {type === 'toggle' && (
        <FormControl fullWidth>
          <Controller
            control={control}
            name="sub_source_type"
            rules={{ required: `Select ${label}` }}
            render={({ field: { onChange } }) => (
              <ToggleButtonGroup
                sx={{
                  '& .MuiToggleButton-root': {
                    padding: '.45rem',
                    minWidth: '3rem'
                  }
                }}
                value={selectedSubSourceType}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) {
                    handleSelectChange(newValue, onChange);
                  }
                }}
                aria-label=""
                disabled={mode === SourceDialogMode.Edit}
              >
                <ToggleButton value="Yes">Yes</ToggleButton>
                <ToggleButton value="No">No</ToggleButton>
              </ToggleButtonGroup>
            )}
          />
          {hasError && <ErrorMessage message={errorMessage} />}
        </FormControl>
      )}
    </Grid>
  );
};

export default SubSourceTypeSelector;
